import { useState, useEffect } from 'react'
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useInfiniteScroll } from '@/lib/hooks/useInfiniteScroll';
import StockOpnameService from '@/lib/services/borrowing/StockOpnameService';

export interface Batch {
  id: number;
  brand: string;
  code: string;
  size: number;
}

interface BatchResponse {
  batches: Batch[];
  hasMore: boolean;
}

export const BatchSelectDropdown = ({ 
  itemId, 
  onSelect, 
  disabled 
}: { 
  itemId: number; 
  onSelect: (batch: Batch) => void;
  disabled?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)
  
  const [batches, setBatches] = useState<Batch[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  const fetchBatches = async (pageNum: number, searchQuery: string, reset: boolean) => {
    setIsFetching(true)
    try {
      const data: BatchResponse = await StockOpnameService.getBatches(itemId, pageNum, searchQuery);
      const receivedBatches = Array.isArray(data?.batches) ? data.batches : [];
      setBatches(prev => reset ? receivedBatches : [...prev, ...receivedBatches])
      setHasMore(data.hasMore)
    } catch (error) {
       console.error(error)
    } finally {
      setIsFetching(false)
    }
  }

  useEffect(() => {
    if (isOpen && !disabled) {
      setPage(1)
      fetchBatches(1, debouncedSearch, true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, isOpen, disabled])

  const lastElementRef = useInfiniteScroll(() => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchBatches(nextPage, debouncedSearch, false)
  }, isFetching, hasMore)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.batch-dropdown-container')) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full batch-dropdown-container">
      <input 
        type="text" 
        placeholder={disabled ? "Requested amount fully allocated" : "Search batch by brand or code..."}
        className={`w-full text-sm border border-border bg-background rounded-md px-3 py-2 transition-colors ${
          disabled ? 'opacity-60 cursor-not-allowed bg-muted' : ''
        }`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={() => !disabled && setIsOpen(true)}
        disabled={disabled}
      />
      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {batches.map((b, index) => {
            const hasStock = Number(b.size) > 0
            return (
              <div
                key={b.id}
                ref={index === batches.length - 1 ? lastElementRef : null}
                aria-disabled={!hasStock}
                className={`p-2 text-sm border-b last:border-0 ${
                  hasStock
                    ? 'hover:bg-secondary cursor-pointer'
                    : 'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (!hasStock) return
                  onSelect(b)
                  setIsOpen(false)
                }}
              >
                <div className="font-semibold">{b.brand} ({b.code})</div>
                <div className="text-xs text-muted-foreground">
                  {hasStock ? `Original Size: ${b.size}` : 'No initial stock'}
                </div>
              </div>
            )
          })}
          {isFetching && <div className="p-2 text-xs text-center text-muted-foreground">Loading...</div>}
          {!isFetching && batches.length === 0 && <div className="p-2 text-xs text-center text-muted-foreground">No batches found</div>}
        </div>
      )}
    </div>
  )
}
